/**
 * Se il telefono è girato in orizzontale, **adesso**.
 *
 * ## Perché è separato da `usePhoneLayout`
 *
 * Perché rispondono a due domande diverse, e confonderle è il difetto appena corretto: «che forma ha
 * questo apparecchio» non cambia ruotandolo — la decide il lato corto — mentre «come lo sta tenendo
 * in mano» cambia eccome, ed è quel che serve qui.
 *
 * Tenerle insieme significava far cambiare disposizione a una rotazione, smontare l'albero e far
 * ripartire il canale da capo.
 *
 * ## Cosa ci si fa
 *
 * Un telefono girato in orizzontale vuole il video a tutto schermo: è il gesto con cui si dice
 * «adesso guardo». In verticale invece l'immagine convive con il resto, e a schermo intero ci si va
 * col tasto apposta.
 */

import { useEffect, useState } from 'react';

export function useLandscape(): boolean {
  const [landscape, setLandscape] = useState(() => misura());

  useEffect(() => {
    const aggiorna = (): void => setLandscape(misura());
    // Anche subito: fra il primo disegno e questo effetto il motore può aver già assestato la
    // pagina, e quel cambiamento non arriverebbe fino al prossimo evento.
    aggiorna();
    window.addEventListener('resize', aggiorna);
    window.addEventListener('orientationchange', aggiorna);
    return () => {
      window.removeEventListener('resize', aggiorna);
      window.removeEventListener('orientationchange', aggiorna);
    };
  }, []);

  return landscape;
}

/**
 * Larghezza contro altezza, e nient'altro.
 *
 * Non `screen.orientation`: non c'è su tutti i motori che questa app deve reggere, e per un browser
 * da scrivania — dove una finestra bassa e larga è «orizzontale» quanto un telefono girato — dice
 * sempre la stessa cosa. Quel che conta è la forma della finestra, che è quel che si misura.
 */
function misura(): boolean {
  if (typeof window === 'undefined') return false;
  const width = window.visualViewport?.width ?? window.innerWidth;
  const height = window.visualViewport?.height ?? window.innerHeight;
  return width > height;
}
